import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useGetAllSkuListQuery,
  useGetMechanicDetailQuery,
  useRemoveMechanicMutation,
} from 'widgets/mechanicDetailPage/api/getMechanicDetailApi';
import { StyledTab, StyledTabList, StyledTabPanel } from 'shared/styles/StyledTabs';
import TabContext from '@mui/lab/TabContext';
import { Box, Button, Typography } from '@mui/material';
import { MoreInfo } from 'shared/ui/MoreInfo/MoreInfo';
import { useAppDispatch, useAppSelector } from 'app/store';
import { setDrawerOpen, setHeaderAction, setHeaderTitle } from 'features/ui/model/uiSlice';
import { StyledCard } from 'shared/ui/Card/StyledCard';
import { TableContentForMechanic } from 'entities/table/ui/TableContentForMechanic';
import { NewHeader } from 'shared/ui/Header/NewHeader';
import { MechanicStepType, MechanicTypeEnum, useSkuValuesByIds } from 'entities/mechanic';
import { MechanicUploadScu } from 'widgets/mechanicDetailPage/ui/MechanicUploadSCU';
import { DataTableFrontendPagination } from 'shared/ui/Table';
import { ColumnConfigType } from 'shared/types';
import { debounce } from 'lodash';

type RenderStep = Pick<MechanicStepType, 'id' | 'reward_type' | 'target'> & { reward: string | number };

type SkuColumnType = {
  sku: string;
};

const fieldsSkuConfig: ColumnConfigType<SkuColumnType>[] = [
  {
    dataFieldName: 'sku',
    name: 'SKU',
    width: '100%',
  },
];

export const MechanicDetailWidget = () => {
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const { actionBtn } = useAppSelector((state) => state.ui);
  const navigate = useNavigate();
  const [searchSku, setSearchSku] = useState([]);

  const [tab, setTab] = useState('1');
  const [drawerVariant, setDrawerVariant] = useState<{
    variant: 'details' | 'sku' | 'steps';
  }>({
    variant: 'details',
  });
  const [renderSteps, setRenderSteps] = useState<RenderStep[]>([]);

  const { data: mechanicDetail } = useGetMechanicDetailQuery({ app_type: 'admin', id: parseInt(id || '') });
  const { data: skuList } = useGetAllSkuListQuery({
    app_type: 'admin',
  });
  const [removeMechanic] = useRemoveMechanicMutation();

  const skus = useSkuValuesByIds(skuList?.skus || [], mechanicDetail?.sku_ids || []);

  const [pagination, setPagination] = useState<{
    page: number;
    count: number;
    search: string;
  }>({
    page: 1,
    count: 10,
    search: '',
  });

  const resetPagination = () => setPagination({ page: 1, count: 10, search: '' });

  const skList = { items: [] as any };

  useEffect(() => {
    setRenderSteps(
      mechanicDetail?.steps
        ? mechanicDetail.steps.map((step) => ({
            ...step,
            reward: step.reward_type === 'prize' ? `Приз номер: ${step.reward}` : (step.reward as number),
          }))
        : [],
    );
  }, [mechanicDetail?.steps]);

  if (pagination.search && skus.length) {
    skus.forEach((item) => {
      if (
        item.title?.toLowerCase().includes(pagination.search.toLowerCase()) ||
        item.sku_id.toLowerCase().includes(pagination.search.toLowerCase())
      ) {
        skList.items.push({
          id: item.id,
          sku_id: item.sku_id,
          title: item.title,
        });
      }
    });
  } else {
    skus?.forEach((c) => {
      skList.items.push({
        id: c.id,
        sku_id: c.sku_id,
        title: c.title,
      });
    });
  }

  const handleChangeTab = (_: React.SyntheticEvent, newTab: string) => setTab(newTab);

  useEffect(() => {
    dispatch(setHeaderAction('custom'));

    return () => {
      dispatch(setHeaderAction(null));
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(setHeaderTitle('Детали механики'));
  }, [dispatch]);

  useEffect(() => {
    if (mechanicDetail && (mechanicDetail as any).skus) {
      setSearchSku((mechanicDetail as any).skus);
    }
  }, [mechanicDetail]);

  const handleRemoveMechanic = () => {
    removeMechanic({ app_type: 'admin', id: mechanicDetail!.id }).then(() => {
      setTimeout(() => {
        navigate('/promo/mechanic');
      }, 2500);
    });
  };

  const onSearchSku = debounce((search) => {
    setSearchSku((mechanicDetail as any).skus.filter((searchSku: string) => searchSku.includes(search)));
  }, 300);

  const MechanicDetailInfoActions = () => (
    <Button
      sx={{ textTransform: 'none', marginRight: '30px', '.MuiSvgIcon-root': { color: '#fff !important' } }}
      variant="contained"
      disabled={mechanicDetail?.is_active === true}
      onClick={handleRemoveMechanic}
    >
      Удалить
    </Button>
  );

  return (
    <>
      {actionBtn === 'custom' && (
        <NewHeader
          title="Детали механики"
          actionSlot={drawerVariant.variant === 'details' ? <MechanicDetailInfoActions /> : null}
        />
      )}

      <TabContext value={tab}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '30px',
          }}
        >
          <StyledTabList onChange={handleChangeTab}>
            <StyledTab
              label="Информация"
              value="1"
              disableRipple
              onClick={() => {
                dispatch(setHeaderAction('custom'));
                setDrawerVariant({ variant: 'details' });
                resetPagination();
              }}
            />
            <StyledTab
              label="SKU"
              value="2"
              disableRipple
              onClick={() => {
                setDrawerVariant({
                  variant: 'sku',
                });
                resetPagination();
              }}
            />
            <StyledTab
              label="Шаги"
              value="3"
              disableRipple
              onClick={() => {
                setDrawerVariant({
                  variant: 'steps',
                });
                resetPagination();
              }}
            />
          </StyledTabList>
        </Box>
        <StyledTabPanel value="1">
          <Box
            component={StyledCard}
            sx={{
              marginBottom: '24px',
            }}
          >
            <Typography>Данные о механике</Typography>
            <Box
              style={{
                display: 'flex',
                marginTop: '12px',
              }}
            >
              <div
                className="column"
                style={{ width: '700px' }}
              >
                <MoreInfo>
                  <div className="title">ID</div>
                  <div className="content">{mechanicDetail?.id ?? '---'}</div>
                </MoreInfo>
                <MoreInfo>
                  <div className="title">Наименование</div>
                  <div className="content">{mechanicDetail?.name ?? '---'}</div>
                </MoreInfo>
                <MoreInfo>
                  <div className="title">Описание</div>
                  <div className="content">{mechanicDetail?.description ?? '---'}</div>
                </MoreInfo>
                <MoreInfo>
                  <div className="title">Статус</div>
                  <div className="content">{mechanicDetail?.is_active ? 'Активная' : 'Неактиваня'}</div>
                </MoreInfo>
                <MoreInfo>
                  <div className="title">Функция расчета</div>
                  <div className="content">{mechanicDetail?.mechanic_type}</div>
                </MoreInfo>
                {(mechanicDetail?.mechanic_type === MechanicTypeEnum.PERCENT ||
                  mechanicDetail?.mechanic_type === MechanicTypeEnum.FIXED) && (
                  <MoreInfo>
                    <div className="title">Циклическая механика</div>
                    <div className="content">{mechanicDetail?.is_cycle ? 'Да' : 'Нет'}</div>
                  </MoreInfo>
                )}
              </div>
            </Box>
          </Box>
        </StyledTabPanel>
        <StyledTabPanel value="2">
          <Button
            variant="outlined"
            onClick={() => dispatch(setDrawerOpen(true))}
            style={{ marginBottom: '24px' }}
          >
            Загрузить EXCEL
          </Button>
          <DataTableFrontendPagination<SkuColumnType>
            title="Список SKU"
            columnConfig={fieldsSkuConfig}
            data={searchSku && searchSku.map((sku) => ({ sku }))}
            search
            onSearch={onSearchSku}
          />
          <MechanicUploadScu id={id} />
        </StyledTabPanel>
        <StyledTabPanel value="3">
          <TableContentForMechanic
            fields={[
              {
                field: 'id',
                title: 'ID',
                width: '20%',
              },
              {
                field: 'target',
                title: 'Цель',
                width: '40%',
              },
              {
                field: 'reward',
                title: 'Бонус',
                width: '40%',
              },
            ]}
            list={{
              list: renderSteps,
            }}
            title="Шаги механики"
            onRowClick={() => {}}
            count={renderSteps.length || 0}
            page={pagination.page - 1}
            perPage={pagination.count}
            onPageChange={(_, page) => {
              setPagination((prev) => ({
                ...prev,
                page: page + 1,
              }));
            }}
            onRowsPerPageChange={(e) => {
              setPagination(() => ({
                page: 1,
                count: parseInt(e.target.value),
                search: pagination.search,
              }));
            }}
          />
        </StyledTabPanel>
      </TabContext>
    </>
  );
};
